import { EthnicOrigin, Gender, Race } from '@/constants/form_inputs';
import {
  IInmate,
  InmateAgeCategory,
  InmateStatus,
} from '@/pages/JMS/Managament/types/inmate';

import { IAddress } from './address';
import { IAuditModel, ObjectId } from './base';
import { IDriverLicense } from './driver.license';
import { IFile } from './file';
import { IPersonFlag } from './flag';
import { IContact } from './user';

export const MasterNameBehaviors = {
  AGGRESSIVE: 'Aggressive',
  ABUSIVE: 'Abusive',
  CALM: 'Calm',
  ['N/A']: 'N/A',
} as const;
export type IMasterNameRelationBehavior = keyof typeof MasterNameBehaviors;

export enum PhoneNumberType {
  HOME = 'HOME',
  MOBILE = 'MOBILE',
  WORK = 'WORK',
  CALLER = 'CALLER',
  OTHER = 'OTHER',
}

export enum PhoneNumberTypeMap {
  HOME = 'Home',
  MOBILE = 'Mobile',
  WORK = 'Work',
  CALLER = 'Caller',
  OTHER = 'Other',
}

export interface IPhoneNumber {
  phoneNumber: string;
  type: PhoneNumberType;
  isPrimary?: boolean;
}

export interface ISocialMedia {
  username: string;
  platform: string;
}

export enum EyeColor {
  BLK = 'Black',
  BLU = 'Blue',
  BRO = 'Brown',
  GRY = 'Gray',
  GRN = 'Green',
  HAZ = 'Hazel',
  MAR = 'Maroon',
  PNK = 'Pink',
  MUL = 'Multi-colored',
  XXX = 'Unknown',
  OTH = 'Other',
}

export enum OtherPhysicalCharacteristics {
  MUSTACHE = 'MUSTACHE',
  BEARED = 'BEARED',
  BIG_EYES = 'BIG_EYES',
  SHORT_HAIR = 'SHORT_HAIR',
  LONG_HAIR = 'LONG_HAIR',
  STRAIGHT_HAIR = 'STRAIGHT_HAIR',
  CURLS = 'CURLS',
  FRECKLES = 'FRECKLES',
}

export interface IPersonalInfo {
  address?: IAddress;
  transient?: boolean;
}

export enum MasterNameType {
  PERSON = 'PERSON',
  ORGANIZATION = 'ORGANIZATION',
}

export interface IPersonalInfoReq extends Omit<IPersonalInfo, 'address'> {
  address?: string;
}

export interface IHeightExact {
  feet?: number;
  inches?: number;
}

export interface IPhysicalCharacteristics {
  weight?: number;
  build?: string;
  skinTone?: string;
  eyeDefects?: string[];
  height?: number;
  heightExact?: IHeightExact;
  eyeColor?: EyeColor;
  hairColor?: string;
  bodyMarking?: string;
  others?: OtherPhysicalCharacteristics[];
}

export interface IEmployerInfo {
  name?: string;
  phoneNumber?: string;
  address?: IAddress;
  occupation?: string;
}

export interface IEmployerInfoReq extends Omit<IEmployerInfo, 'address'> {
  address?: string;
}

export interface IMasterNameBase extends IAuditModel {
  type: MasterNameType;
  files?: IFile[];
  isVerified?: boolean;
  citizenOf?: string;
  address?: IAddress;
  addresses?: IAddress[];
  phoneNumbers?: IPhoneNumber[];
  nationality?: string;
  spokenLanguages?: string[];
  socialMedia?: ISocialMedia[];
  gangAffiliations?: string;
  usMarshallNumber?: string;
  // We can create a MasterName without any info. In this case, isUnknown is true.
  isUnknown?: boolean;
  name: string;

  kind?: 'MasterNamePerson' | 'MasterNameOrganization';
  KaseMasterName?: any;
}

export interface IMasterNamePerson extends IMasterNameBase, ObjectId {
  suffix?: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  maidenName?: string;
  aliasName?: string;
  nickName?: string;
  emergencyContacts?: IEmergencyContact[];
  sex?: Gender;
  birthday?: string;
  placeOfBirth?: string;
  ssn?: string;
  nysid?: string;
  fbiNumber?: string;
  refNumber?: string;
  driverLicense?: IDriverLicense;
  race?: Race;
  employerInfo?: IEmployerInfo;
  physicalCharacteristics?: IPhysicalCharacteristics;
  ethnicOrigin?: EthnicOrigin;
  maritalStatus?: string;
  education?: string;
  religion?: string;
  ageCategory?: InmateAgeCategory;
  inmateStatus?: InmateStatus;
  uniqueId?: string | number;

  linkedCFSs?: number;
  emails?: string[];
  type: MasterNameType.PERSON;
  inmate?: IInmate;
  isWanted?: boolean;
  isEmployee?: boolean;
  transient?: boolean;
  flags?: IPersonFlag[]; // Array of assigned flags
  flagCodes?: string[]; // ["WP", "ESCR", "KS"] for quick display
  idScanVerifiedFields?: Record<string, any>;
}

export interface IMasterNameReq
  extends Omit<
    IMasterNamePerson,
    | 'name'
    | 'employerInfo'
    | 'driverLicense'
    | 'address'
    | 'createdAt'
    | 'updatedAt'
    | 'type'
  > {
  employerInfo?: IEmployerInfoReq;
  driverLicense?: string;
  address?: string;
}

export type IMasterName = IMasterNamePerson | IMasterNameOrganization;
export const OrganizationType = {
  BUSINESS: 'Business',
  FINANCIAL_INSTITUTION: 'Financial institution',
  GOVERNMENT: 'Government',
  RELIGIOUS_ORGANIZATION: 'Religious organization',
  SOCIETY_PUBLIC: 'Society/Public',
  OTHER: 'Other',
  UNKNOWN: 'Unknown',
};
export type IOrganizationType = keyof typeof OrganizationType;

export interface IMasterNameOrganization extends IMasterNameBase {
  type: MasterNameType.ORGANIZATION;
  emails?: string[];
  contacts?: IContact[];
  urls?: string[];
  notes?: string;
  socialMediaLinks?: string[];
  organizationType?: IOrganizationType;
  emergencyContacts?: IEmergencyContact[];
  uniqueId?: string | number;
}

export type IMasterOrganizationRequest = Omit<
  IMasterNameOrganization,
  'address'
> & {
  address?: string;
};

export type IMasterNameOrganizationForm = IMasterNameOrganization & {
  informationType: Array<Record<string, any>>;
};

export enum EmergencyContactRelationship {
  PARENT = 'Parent',
  CHILD = 'Child',
  SIBLING = 'Sibling',
  SPOUSE_PARTNER = 'Spouse/Partner',
  FIANCE_FIANCEE = 'Fiancé/Fiancée',
  BOYFRIEND_GIRLFRIEND = 'Boyfriend/Girlfriend',
  FRIEND = 'Friend',
  NEIGHBOR = 'Neighbor',
  ROOMMATE = 'Roommate',
  COLLEAGUE_WORK_FRIEND = 'Colleague/Work Friend',
  MENTOR = 'Mentor',
  TEACHER = 'Teacher',
  STUDENT = 'Student',
  BOSS_MANAGER = 'Boss/Manager',
  EMPLOYEE = 'Employee',
  CLIENT = 'Client',
  DOCTOR = 'Doctor',
  THERAPIST = 'Therapist',
  LAWYER = 'Lawyer',
  RELIGIOUS_LEADER = 'Religious Leader (e.g., Pastor, Priest, Imam)',
  COMMUNITY_LEADER = 'Community Leader',
  COUSIN = 'Cousin',
  AUNT_UNCLE = 'Aunt/Uncle',
  GRANDPARENT = 'Grandparent',
  GRANDCHILD = 'Grandchild',
  NIECE_NEPHEW = 'Niece/Nephew',
  ACQUAINTANCE = 'Acquaintance',
  OTHER = 'Other',
}

export interface IEmergencyContactRelationship {
  isPrimary?: boolean | null;
  relationship?: EmergencyContactRelationship | null;
}

export interface IEmergencyContact extends IAuditModel {
  emergencyContact: IMasterNamePerson;
  relationship?: IEmergencyContactRelationship | null;
}

export interface AddNewEmergencyContactForm {
  firstName: string;
  middleName: string;
  lastName: string;
  phoneNumbers?: IPhoneNumber[];
  isPrimary: boolean;
  addresses?: IAddress[];
  transient?: boolean;
  relationship: EmergencyContactRelationship;
  emails?: string[] | { email: string }[];
}
